/*
 * Dialog: a modal for a focused task. Centred on desktop, a bottom sheet on phones. Focus is trapped and
 * returned; Escape and the close button dismiss.
 *   <Dialog open={open} onOpenChange={setOpen} title={…} description={…}
 *     footer={<><Button variant="secondary" onClick={close}>…</Button><Button>…</Button></>}>
 *     …body…
 *   </Dialog>
 * Or uncontrolled with a trigger: <Dialog trigger={<Button>…</Button>} …>.
 */
import { X } from '@phosphor-icons/react'
import { Dialog as D } from 'radix-ui'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'

export function Dialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
}: {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: ReactNode
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** false: no close button, Escape and outside clicks do nothing (e.g. the session-expired dialog) */
  dismissible?: boolean
  className?: string
}) {
  const { t } = useTranslation('common')
  const width = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' }[size]
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      {trigger ? <D.Trigger asChild>{trigger}</D.Trigger> : null}
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-scrim backdrop-blur-[2px] data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
        <D.Content
          onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
          onPointerDownOutside={dismissible ? undefined : (e) => e.preventDefault()}
          onInteractOutside={dismissible ? undefined : (e) => e.preventDefault()}
          className={cn(
            'fixed z-50 flex max-h-[92dvh] w-full flex-col bg-surface shadow-float outline-none',
            'inset-x-0 bottom-0 rounded-t-[24px] data-[state=open]:animate-[toast-in_260ms_var(--ease-out-soft)]',
            'sm:inset-0 sm:m-auto sm:h-fit sm:w-[calc(100%-2rem)] sm:rounded-panel sm:ring-1 sm:ring-card-line sm:data-[state=open]:animate-pop-in',
            width,
            className,
          )}
        >
          <div className="flex items-start gap-4 px-5 pt-5 sm:px-6 sm:pt-6">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <D.Title className="text-18 font-semibold text-ink">{title}</D.Title>
              {description ? (
                <D.Description className="text-14 leading-6 text-muted">{description}</D.Description>
              ) : (
                <D.Description className="sr-only">{title}</D.Description>
              )}
            </div>
            {dismissible ? (
              <D.Close
                aria-label={t('actions.close')}
                className="-me-2 -mt-1 grid size-9 shrink-0 place-items-center rounded-xl text-muted transition-colors hover:bg-control hover:text-ink touch:size-11"
              >
                <X className="size-[18px]" aria-hidden="true" />
              </D.Close>
            ) : null}
          </div>
          {children ? (
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">{children}</div>
          ) : (
            <div className="h-5" />
          )}
          {footer ? (
            <div className="flex flex-col-reverse gap-2 border-t border-hairline px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:px-6 sm:pb-4 [&>*]:w-full sm:[&>*]:w-auto">
              {footer}
            </div>
          ) : null}
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}

export const DialogClose = D.Close
